import React from "react";
import { Typography } from "antd";
import type { CategoryDTO } from "api";
import {
  LaptopOutlined,
  ScissorOutlined,
  HeartOutlined,
  SmileOutlined,
  ThunderboltOutlined,
  SkinOutlined,
  CameraOutlined,
  BookOutlined,
  GlobalOutlined,
  ReadOutlined,
  BulbOutlined,
  FireOutlined,
  MedicineBoxOutlined,
  HomeOutlined,
  ToolOutlined,
  GiftOutlined,
  CalendarOutlined,
  PictureOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Routes from "src/constants/routes";

import {
  StyledCategoryLink,
  CategoryCard,
  BackgroundDecoration,
  IconWrapper,
  ContentWrapper,
  CategoryTitle,
  ArrowIcon,
} from "./Category.styles";

export interface CategoryProps {
  category: CategoryDTO;
}

const getIconForCategory = (categoryName: string) => {
  const name = categoryName.toLowerCase();

  // Beauty
  if (name.includes("barber")) return <ScissorOutlined />;

  if (name.includes("hair_stylist")) return <ScissorOutlined />;

  if (name.includes("nail")) return <SkinOutlined />;

  if (name.includes("brow") || name.includes("lash")) return <SmileOutlined />;

  // Health & Wellness
  if (name.includes("massage")) return <HeartOutlined />;

  if (name.includes("personal_trainer")) return <ThunderboltOutlined />;

  if (name.includes("yoga") || name.includes("pilates")) return <FireOutlined />;

  if (name.includes("nutritionist")) return <MedicineBoxOutlined />;

  if (name.includes("physical_therapist")) return <MedicineBoxOutlined />;

  // Education
  if (name.includes("tutor")) return <BookOutlined />;

  if (name.includes("language")) return <GlobalOutlined />;

  if (name.includes("music")) return <ReadOutlined />;

  if (name.includes("art") || name.includes("drawing")) return <BulbOutlined />;

  if (name.includes("test_prep")) return <ReadOutlined />;

  // Home & Lifestyle
  if (name.includes("cleaning")) return <HomeOutlined />;

  if (name.includes("organizer")) return <HomeOutlined />;

  if (name.includes("repair") || name.includes("handym")) return <ToolOutlined />;

  if (name.includes("tailor") || name.includes("sewing")) return <ToolOutlined />;

  // Events & Creative
  if (name.includes("photograph")) return <CameraOutlined />;

  if (name.includes("videograph")) return <CameraOutlined />;

  if (name.includes("event_planner")) return <CalendarOutlined />;

  if (name.includes("dj") || name.includes("host")) return <GiftOutlined />;

  if (name.includes("decorator")) return <PictureOutlined />;

  // Pet Services
  if (name.includes("dog") || name.includes("pet") || name.includes("groomer")) return <HeartOutlined />;

  if (name.includes("trainer") && name.includes("pet")) return <HeartOutlined />;

  if (name.includes("sitter")) return <HeartOutlined />;

  // Business & Tech
  if (name.includes("designer") || name.includes("developer")) return <LaptopOutlined />;

  if (name.includes("marketing")) return <BulbOutlined />;

  if (name.includes("virtual_assistant")) return <LaptopOutlined />;

  if (name.includes("resume")) return <BookOutlined />;

  return <LaptopOutlined />;
};

const Category: React.FC<CategoryProps> = ({ category }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <StyledCategoryLink to={Routes.categoryBusinesses.replace(":id", String(category.id))}>
      <CategoryCard
        $isHovered={isHovered}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <BackgroundDecoration />

        <IconWrapper $isHovered={isHovered}>{getIconForCategory(category.name || "")}</IconWrapper>

        <ContentWrapper>
          <CategoryTitle $isHovered={isHovered} as={Typography.Title} level={4} ellipsis>
            {t(`categories.${category.name}`)}
          </CategoryTitle>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {t("home.categories.topRated")}
          </Typography.Text>
        </ContentWrapper>

        <ArrowIcon $isHovered={isHovered}>
          <RightOutlined />
        </ArrowIcon>
      </CategoryCard>
    </StyledCategoryLink>
  );
};

export default Category;
